import { useCallback, useRef } from 'react';
import { useToast } from '@sorrem-client/ui/components';
import { useDeveloperAccess } from '@sorrem-client/ui/hooks';
import { captureTabSnapshot, findCleanRenderSource, saveSnapshot, buildSnapshotFileName, type SnapshotKind } from '@sorrem-client/ui/snapshot';

/** A clean render's size, as a multiple of the canvas: sharp enough for the website and Open Graph images. */
const CLEAN_RENDER_SCALE = 2;

/** Short, so a confirmation is gone before the next shot is framed. */
const SAVED_TOAST_MS = 1500;

export interface DeveloperSnapshot {
    /** Whether this session may take snapshots: developers only. */
    readonly isAvailable: boolean;
    /** Saves the tab as it looks, HUD and all. The first one in a session asks Chrome to share the tab. */
    readonly takeSnapshot: () => void;
    /** Saves the 3D scene on screen alone, without the HUD, at twice the canvas size. */
    readonly takeCleanRender: () => void;
}

/**
 * Developer snapshots (#303): marketing shots from the live game, saved to
 * the browser's downloads as PNGs. One capture runs at a time; a press while
 * one is under way is ignored. The confirmation toast shows only after the
 * picture is taken, so it is never in it.
 */
export function useDeveloperSnapshot(): DeveloperSnapshot {
    const { isDeveloper } = useDeveloperAccess();
    const { showToast } = useToast();
    const isCapturingRef = useRef(false);

    const capture = useCallback((kind: SnapshotKind, take: () => Promise<Blob | null>): void => {
        if (!isDeveloper || isCapturingRef.current) {
            return;
        }
        isCapturingRef.current = true;
        const takenAt = new Date();
        take()
            .then((blob) => {
                if (!blob) {
                    return;
                }
                const fileName = buildSnapshotFileName(kind, takenAt);
                saveSnapshot(blob, fileName);
                showToast({ message: `Saved ${fileName}`, severity: 'info', compact: true, durationMs: SAVED_TOAST_MS });
            })
            .catch((error: unknown) => {
                const reason = error instanceof Error ? error.message : String(error);
                showToast({ message: `Snapshot failed: ${reason}`, severity: 'error' });
            })
            .finally(() => {
                isCapturingRef.current = false;
            });
    }, [isDeveloper, showToast]);

    const takeSnapshot = useCallback((): void => {
        // Declining Chrome's share prompt resolves to null: nothing to save, nothing to report.
        capture('snapshot', captureTabSnapshot);
    }, [capture]);

    const takeCleanRender = useCallback((): void => {
        capture('clean', async () => {
            const source = findCleanRenderSource();
            if (!source) {
                throw new Error('No 3D scene is on screen.');
            }
            const result = await source.capture(CLEAN_RENDER_SCALE);
            if (!result.ok) {
                throw new Error(result.reason);
            }
            return new Blob([result.png], { type: 'image/png' });
        });
    }, [capture]);

    return { isAvailable: isDeveloper, takeSnapshot, takeCleanRender };
}
